import { useMsal } from '@azure/msal-react';
import { useHeap } from '@configs/heap';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';

import type { RecordsRouterProps } from './RecordsRouterPage.types';

/**
 * RecordsRouterPage - MFE wrapper for Records
 *
 * This page loads the remote RecordsRouter MFE and passes
 * the required props. The MFE handles its own internal routing.
 *
 * Guard stack (applied in manage.routes.tsx):
 * 1. AuthenticatedRoute - User must be logged in
 * 2. OrgRouteGuard - Validate org access
 * 3. PermissionGuard - Records_View_Org required
 *
 * @see docs/ROUTE_ARCHITECTURE.md for guard documentation
 */
const RecordsRouterPage = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { selectedOrganization } = useOrganizations();
  const { permissions } = usePermissions();
  const getToken = useGetToken();
  const heapIdentity = useHeap();

  const [RemoteRouter, setRemoteRouter] =
    React.useState<React.ComponentType<RecordsRouterProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const loadRouter = async (): Promise<void> => {
      try {
        const { default: Router } =
          await import('reservations_mfe/RecordsRouter');
        setRemoteRouter(() => Router);
      } catch (error) {
        console.error('Error loading RecordsRouter:', error);
        setLoadError(error as Error);
      }
    };

    loadRouter();
  }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!RemoteRouter || !selectedOrganization || !heapIdentity) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteRouter
        msalInstance={instance}
        user={authData}
        organizationId={selectedOrganization.id}
        permissions={permissions}
        heapIdentity={heapIdentity}
        getToken={getToken}
      />
    </React.Suspense>
  );
});

RecordsRouterPage.displayName = 'RecordsRouterPage';

export { RecordsRouterPage };
